/*
  ERO Cart Drawer — wiring (workbook 6.2, prompt 2)
  Spec: docs/ERO_CART_DRAWER_SPEC.md sections 2 and 3

  Owns five things and nothing else:
    1. ATC interception for the PDP buy box form and the homepage buy module
       forms, so both open the drawer instead of redirecting to /cart.
    2. A bridge from the sticky ATC bar's existing cartUpdate publish to a
       drawer open, so that section needs no edits at all.
    3. Mobile sheet heights: compact <-> expanded <-> closed, Pointer Events.
    4. Keeping <cart-drawer>'s is-empty class honest after a render.
    5. The free gift line (syncGift, Nov 2 2026).

  Dawn's engine is untouched. assets/cart-drawer.js still owns open/close,
  focus trapping, Escape, the overlay click and section rendering;
  assets/cart.js still owns quantity, remove and line item errors.

  6.16: the line controls, nudge and add-ons also run on the /cart page,
  which has no drawer. getSurface() picks the drawer whenever one exists,
  so every page that mounts the drawer behaves exactly as before.

  DELIBERATELY NOT HANDLED HERE: the PDP cross-sell. It publishes cartUpdate
  with source 'pdp-cross-sell' and is filtered out of the open path below, so
  a shaker or sample add keeps its in-place ADDED state and never bounces the
  drawer (spec section 2, "Open triggers").
*/
(function () {
  'use strict';

  /* The drawer's render set. ATC interception always posts exactly this;
     in-cart writes and refreshes use sectionIds(), which equals it whenever
     the drawer is mounted. */
  var SECTIONS = 'cart-drawer,cart-icon-bubble';
  var MOBILE_QUERY = '(max-width: 989px)';
  /* 24, down from 40: with live follow the sheet is already under the
     finger, so the release only has to confirm intent, not carry it. */
  var DRAG_THRESHOLD = 24;

  /* px/ms at release. A flick shorter than the distance threshold still
     snaps if it was fast enough. */
  var VELOCITY_THRESHOLD = 0.3;

  /* Vertical travel that turns a press into a drag. Below this a gesture is
     still a tap and every control behaves normally. */
  var DRAG_SLOP = 8;

  /* A drag ends with a synthetic click on whatever the finger started on.
     Swallowed within this window so a swipe across a pill never selects it. */
  var CLICK_SUPPRESS_MS = 400;

  var ADDED_MS = 1600;

  /* Forms whose submit is hijacked. All expose [data-atc] and
     [data-atc-label], so one handler covers the PDP buy box (which is also
     the homepage buy box) and the 6.3 collection page. The collection page
     has four ATC surfaces and writes no cart code of its own: it relies
     entirely on this client for the add, the busy state, the ADDED! swap
     and the drawer open. */
  var ATC_FORMS =
    '[data-ero-pdp-buy-box] form.bb-form, .ero-collection__form';

  /* ── Portal to body ───────────────────────────────────────────────
     assets/ero-starfield.css gives every .shopify-section
     `position: relative; z-index: 5`, so the wrapper emitted by
     {% section 'cart-drawer' %} is a z-index: 5 stacking context. That
     trapped the drawer two ways: its own z-index only competed against
     other sections (and the nav, later in document order, won), and the
     page sections painted above it, leaving the panel's backdrop-filter
     nothing to sample. Moving the element onto body escapes the wrapper
     and fixes both.

     Runs BEFORE Dawn's CartDrawer constructor: this file is deferred from
     snippets/cart-drawer.liquid, cart-drawer.js is deferred later from
     layout/theme.liquid, and deferred scripts execute in document order.
     Moving the element before it upgrades means no disconnected/connected
     callback churn on <cart-drawer-items>.

     Same pattern, same reason as the sticky ATC bar. */
  function portalToBody() {
    var drawers = document.querySelectorAll('cart-drawer');
    if (!drawers.length) return;

    /* A theme editor re-render rebuilds the section wrapper and puts a
       fresh <cart-drawer> inside it while the portaled one is still on
       body. Newest wins, the rest go, so no duplicate ever survives. */
    var keep = drawers[drawers.length - 1];
    for (var i = 0; i < drawers.length; i += 1) {
      if (drawers[i] !== keep) drawers[i].remove();
    }

    if (keep.parentElement !== document.body) document.body.appendChild(keep);
  }

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function getDrawer() {
    return document.querySelector('cart-drawer');
  }

  function canRender(drawer) {
    return !!drawer && typeof drawer.renderContents === 'function';
  }

  /* ── Surfaces ─────────────────────────────────────────────────────
     The cart controls live on one of two surfaces: the drawer, or the
     /cart page root (6.16), which is never mounted alongside a drawer.
     The drawer is checked first, so on any page that has one, nothing
     downstream of this takes a different branch than it did before. */
  function getSurface() {
    var drawer = getDrawer();
    if (drawer) {
      return { root: drawer, section: 'cart-drawer', blockId: 'CartDrawer', isDrawer: true };
    }
    var page = document.querySelector('[data-ero-cart-root]');
    if (page) {
      return { root: page, section: page.dataset.eroCartSection, blockId: 'EroCartPage', isDrawer: false };
    }
    return null;
  }

  /* The surface's own section plus the header bubble. SECTIONS when the
     drawer is mounted, or when no surface (or no page section id) exists. */
  function sectionIds() {
    var surface = getSurface();
    return surface && surface.section ? surface.section + ',cart-icon-bubble' : SECTIONS;
  }

  /* ── Empty state ──────────────────────────────────────────────────
     Dawn's renderContents strips is-empty from .drawer__inner and then
     replaces it wholesale, but it never touches the class on <cart-drawer>
     itself — only the quantity path in cart.js does. Left alone, a cart that
     goes from empty to one line keeps .is-empty on the host, and the sheet
     hugs its content instead of resting at the 448 compact height. */
  function syncEmptyState() {
    var drawer = getDrawer();
    if (!drawer) return;

    var empty = !drawer.querySelector('.ero-cart__lines');
    drawer.classList.toggle('is-empty', empty);

    var inner = drawer.querySelector('.drawer__inner');
    if (inner) inner.classList.toggle('drawer__inner-empty', empty);
  }

  /* ── Live follow ──────────────────────────────────────────────────
     The sheet tracks the finger by animating HEIGHT only. No transform ever
     goes on .drawer__inner: it carries the ::before glass, and a transform
     there kills the backdrop-filter and every nested glow layer.

     MUST TRACK assets/ero-tokens.css: --u is min(100vw, 480px) / 375, and
     the two resting heights are 448u (compact) and 100dvh - 24u (expanded),
     matching .ero-cart and the is-expanded rule in ero-cart-drawer.css. */
  function unitPx() {
    return Math.min(window.innerWidth, 480) / 375;
  }

  function compactPx() {
    return 448 * unitPx();
  }

  function expandedPx() {
    return window.innerHeight - 24 * unitPx();
  }

  function beginFollow(started, drawer, inner) {
    started.startHeight = inner.getBoundingClientRect().height;
    started.minH = Math.min(compactPx(), started.startHeight);
    started.maxH = Math.max(expandedPx(), started.startHeight);
    /* Suppresses the 260ms height transition so the sheet sits under the
       finger instead of chasing it. */
    drawer.classList.add('is-dragging');
  }

  function follow(started, inner) {
    if (typeof started.startHeight !== 'number') return;
    var grown = started.startY - started.lastY;
    var next = started.startHeight + grown;
    if (next < started.minH) next = started.minH;
    if (next > started.maxH) next = started.maxH;
    inner.style.height = next + 'px';
  }

  function endFollow(drawer, inner) {
    if (drawer) drawer.classList.remove('is-dragging');
    /* Clearing the inline height AFTER the transition is restored lets the
       snap animate from wherever the finger left it to the class height. */
    if (inner) inner.style.height = '';
  }

  /* ── Sheet height ─────────────────────────────────────────────────
     Nothing swaps between the two heights. is-expanded is the only state,
     and it is meaningless on desktop, which has a single resting height. */
  function setSheet(mode) {
    var drawer = getDrawer();
    if (!drawer) return;

    if (!isMobile()) {
      drawer.classList.remove('is-expanded');
      return;
    }
    drawer.classList.toggle('is-expanded', mode === 'expanded');
  }

  function closeDrawer() {
    var drawer = getDrawer();
    if (drawer && typeof drawer.close === 'function') drawer.close();
  }

  /* ── Body scroll lock ─────────────────────────────────────────────
     Dawn only adds body.overflow-hidden, and iOS Safari ignores
     `overflow: hidden` on body for touch scrolling — the page scrolled
     behind the sheet and stole the gesture from the sheet's own scroller.
     position: fixed is the lock WebKit actually honours, so the offset has
     to be stashed and restored by hand.

     Driven off a MutationObserver on the host's class list rather than by
     patching cart-drawer.js: Dawn owns .active, and the engine stays
     untouched (spec section 2). */
  var lockedScrollY = 0;
  var isLocked = false;

  function lockBody() {
    if (isLocked) return;
    isLocked = true;
    lockedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.documentElement.style.setProperty('--ero-cart-scroll-y', lockedScrollY + 'px');
    document.body.classList.add('ero-cart-locked');
  }

  function unlockBody() {
    if (!isLocked) return;
    isLocked = false;
    document.body.classList.remove('ero-cart-locked');
    document.documentElement.style.removeProperty('--ero-cart-scroll-y');
    /* 'instant' so the restore never animates past the drawer's own close. */
    window.scrollTo({ top: lockedScrollY, left: 0, behavior: 'instant' });
  }

  function bindScrollLock() {
    var drawer = getDrawer();
    if (!drawer || drawer._eroCartLockBound) return;
    drawer._eroCartLockBound = true;

    var resetTimer = null;

    var sync = function () {
      if (drawer.classList.contains('active')) {
        if (resetTimer) {
          window.clearTimeout(resetTimer);
          resetTimer = null;
        }
        lockBody();
        return;
      }

      unlockBody();

      /* Reset the resting height to compact, but only AFTER the slide-out
         has finished: height is a transitioned property, so clearing it
         immediately would shrink the sheet on screen while it is leaving.
         Every open path sets the height explicitly, so this is belt and
         braces against a future path that forgets. */
      if (resetTimer) window.clearTimeout(resetTimer);
      resetTimer = window.setTimeout(function () {
        drawer.classList.remove('is-expanded');
        resetTimer = null;
      }, 360);
    };

    new MutationObserver(sync).observe(drawer, {
      attributes: true,
      attributeFilter: ['class']
    });
    sync();
  }

  /* ── Section rendering ────────────────────────────────────────────
     ?sections= returns a flat { id: html } map; renderContents expects that
     map nested under a `sections` key, which is the shape /cart/add.js
     already replies with. */
  function fetchSections() {
    /* Surface render set, so a quiet refresh on /cart re-renders the page.
       No Accept: application/json here: on /products/* and /cart, Shopify answers
       that header with the product or cart JSON and ignores ?sections=. */
    return fetch(window.location.pathname + '?sections=' + sectionIds()).then(function (response) {
      if (!response.ok) throw new Error('Section render failed.');
      return response.json();
    });
  }

  function openWith(payload, mode) {
    var drawer = getDrawer();
    if (!canRender(drawer)) return;

    /* Height first: the class lives on the host, outside the markup
       renderContents is about to replace, so it survives the swap and the
       sheet is already the right height at first paint. */
    setSheet(mode);
    drawer.renderContents(payload);
    syncEmptyState();
  }

  function refreshAndOpen(mode) {
    return fetchSections().then(function (sections) {
      openWith({ sections: sections }, mode);
    });
  }

  /*
     Paint an already-fetched sections map without opening the drawer.

     Deliberately NOT renderContents for in-drawer updates. renderContents
     ends by calling open(), which re-runs Dawn's trapFocus and pulls focus
     onto .drawer__inner — so every pill tap would yank focus off the control
     the user just pressed, and on mobile that can jump the sheet. The drawer
     is already open in all of these paths, so there is nothing to open.

     Everything renderContents rebuilds is rebuilt here: the #CartDrawer
     subtree, the header bubble, and the overlay's close listener, which
     lived inside the replaced markup. Delegated handlers live on
     <cart-drawer> itself and are untouched.
  */
  function applySections(sections) {
    if (!sections) return;

    /* Swaps the surface's block: #CartDrawer in the drawer, #EroCartPage on
       /cart. The overlay and the drawer-only empty-state sync below need
       isDrawer. */
    var surface = getSurface();
    var stage = surface && document.getElementById(surface.blockId);
    var html = surface && sections[surface.section];
    if (stage && html) {
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var fresh = parsed.querySelector('#' + surface.blockId);
      if (fresh) {
        stage.innerHTML = fresh.innerHTML;

        if (surface.isDrawer) {
          var overlay = document.getElementById('CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', closeDrawer);
        }
      }
    }

    var bubble = document.getElementById('cart-icon-bubble');
    var bubbleHtml = sections['cart-icon-bubble'];
    if (bubble && bubbleHtml) {
      var bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
      var section = bubbleDoc.querySelector('.shopify-section');
      if (section) bubble.innerHTML = section.innerHTML;
    }

    if (surface && surface.isDrawer) syncEmptyState();
  }

  /* Re-render without opening. Used when something else changed the cart
     (the cross-sell) so a drawer opened later is never stale. */
  function refreshQuiet() {
    return fetchSections().then(applySections);
  }

  /* ── Cart writes ──────────────────────────────────────────────────
     One JSON poster for change.js and add.js. Both accept `sections` and
     reply with the rendered map alongside the cart, so a control update is
     a single round trip. */
  function postCart(path, body) {
    /* Surface render set: the reply carries whichever block applySections swaps. */
    body.sections = sectionIds();
    body.sections_url = window.location.pathname;

    return fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Cart update failed.');
        }
        return data;
      });
    });
  }

  /* ── Free gift (Taylor ruling Nov 2 2026) ─────────────────────────
     One free gift line: a cart whose only bags are Blue Raspberry gets the
     Peach Mango samples, only Peach Mango gets the Blue Raspberry samples.
     Both flavors, any bundle, or no bag gets none. Quantity is always 1.
     With the Theme settings switch off (config.enabled false) every cart gets
     none, so gifts already in carts come out on the next sync.

     syncGift compares the live cart to that rule and fixes only gift lines.
     It never rejects and never publishes cartUpdate, so it cannot block the
     cart or checkout and cannot loop. Overlapping calls queue ONE re-run
     behind the run in flight, and that re-run reads the cart afresh.

     It runs at the END of each write path (settleGift, syncGiftQuiet), never
     inside postCart. swapVariant makes a change.js call and then reuses the
     same line number in its fallback, and runNudge is remove-then-add, so a
     sync between those steps could shift the lines under them and remove the
     wrong one. */
  var GIFT_CHECKOUT_WAIT_MS = 3000;
  var giftConfigRead = false;
  var giftConfigValue = null;
  var giftRun = null;
  var giftQueued = null;

  function giftConfig() {
    if (giftConfigRead) return giftConfigValue;
    giftConfigRead = true;

    var node = document.getElementById('EroGiftConfig');
    if (!node) return null;
    try {
      var config = JSON.parse(node.textContent);
      /* Neither gift product exists, so no gift line can be in a cart and there
         is nothing to add or clear. Null keeps every caller out of the way. */
      if (config.gifts.pm.handle || config.gifts.br.handle) giftConfigValue = config;
    } catch (error) {
      console.error(error);
    }
    return giftConfigValue;
  }

  function runGiftSync(config) {
    var gifts = config.gifts;
    var handles = config.handles;
    var changed = false;

    function write(path, body) {
      return fetch(path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      }).then(function (response) {
        if (!response.ok) throw new Error('Free gift update failed: ' + response.status);
        changed = true;
      });
    }

    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Free gift could not read the cart: ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        var hasBr = false;
        var hasPm = false;
        var hasBundle = false;
        var giftLines = [];

        (cart.items || []).forEach(function (item) {
          if (item.handle === handles.br) hasBr = true;
          else if (item.handle === handles.pm) hasPm = true;
          else if (item.handle === handles.bundle) hasBundle = true;
          else if (item.handle === gifts.pm.handle) giftLines.push({ flavor: 'pm', key: item.key, quantity: item.quantity });
          else if (item.handle === gifts.br.handle) giftLines.push({ flavor: 'br', key: item.key, quantity: item.quantity });
        });

        /* Switched off, wanted stays null: every gift line goes, nothing is added. */
        var wanted = null;
        if (config.enabled) {
          if (hasBr && !hasPm && !hasBundle) wanted = 'pm';
          else if (hasPm && !hasBr && !hasBundle) wanted = 'br';
        }
        if (wanted &&!(gifts[wanted].available && gifts[wanted].variantId)) wanted = null;

        var kept = false;
        var steps = Promise.resolve();

        giftLines.forEach(function (line) {
          var keep = line.flavor === wanted && !kept;
          if (keep) kept = true;
          if (keep && line.quantity === 1) return;
          steps = steps.then(function () {
            return write('/cart/change.js', { id: line.key, quantity: keep ? 1 : 0 });
          });
        });

        if (wanted && !kept) {
          steps = steps.then(function () {
            return write('/cart/add.js', { items: [{ id: gifts[wanted].variantId, quantity: 1 }] });
          });
        }

        return steps;
      })
      .then(function () {
        return changed;
      })
      .catch(function (error) {
        console.error(error);
        /* false unless an earlier write in this run already landed: then the
           cart did change, and the surface has to repaint to show it. */
        return changed;
      });
  }

  function syncGift() {
    var config = giftConfig();
    if (!config) return Promise.resolve(false);

    if (giftRun) {
      if (!giftQueued) {
        giftQueued = giftRun.then(function () {
          giftQueued = null;
          return syncGift();
        });
      }
      return giftQueued;
    }

    giftRun = runGiftSync(config).then(function (changed) {
      giftRun = null;
      return changed;
    });
    return giftRun;
  }

  /* For a write path that is about to paint: settles the gift first and hands
     back sections that include it. If the re-fetch fails, the write's own
     sections still paint and the gift shows on the next render. */
  function settleGift(sections) {
    return syncGift().then(function (changed) {
      if (!changed) return sections;
      return fetchSections().catch(function (error) {
        console.error(error);
        return sections;
      });
    });
  }

  /* For a path that has already painted, or paints nothing. */
  function syncGiftQuiet() {
    return syncGift()
      .then(function (changed) {
        if (changed) return refreshQuiet();
      })
      .catch(function (error) {
        console.error(error);
      });
  }

  /* Before checkout. Delegated on document, so one listener survives every
     re-render, and in the bubble phase, so a click the drawer's drag guard
     swallowed never gets here. The release is a second click on the button
     rather than form.submit(), which would leave out the button's
     name="checkout" and land on /cart. The wait is capped, so a stalled
     network can never hold the shopper at the button. Quantity inputs are
     keyed by line (snippets/ero-cart-lines.liquid), so a form painted before
     this sync still posts the right quantities. */
  var giftCheckoutWaiting = false;
  var giftCheckoutReleased = false;

  function onGiftCheckoutClick(event) {
    var target = event.target;
    if (!target || !target.closest || !target.closest('#CartDrawer-Checkout')) return;
    if (giftCheckoutReleased) return;

    if (!giftConfig()) return;

    event.preventDefault();
    if (giftCheckoutWaiting) return;
    giftCheckoutWaiting = true;

    var waited = new Promise(function (resolve) {
      window.setTimeout(resolve, GIFT_CHECKOUT_WAIT_MS);
    });

    Promise.race([syncGift(), waited]).then(function () {
      giftCheckoutWaiting = false;
      /* Looked up again: a render during the wait may have replaced it. */
      var button = document.getElementById('CartDrawer-Checkout');
      if (!button) return;
      giftCheckoutReleased = true;
      button.click();
      giftCheckoutReleased = false;
    });
  }

  /* ── ATC forms ────────────────────────────────────────────────────
     Neither the buy box nor the buy module ships an error node or a loading
     state, so both are created here rather than by editing those sections.
     .ero-cart-form-error is styled in assets/ero-cart-drawer.css, which is
     loaded site-wide alongside the drawer. */
  function errorNode(form) {
    var node = form.querySelector('[data-ero-cart-error]');
    if (node) return node;

    node = document.createElement('p');
    node.className = 'ero-cart-form-error';
    node.setAttribute('role', 'alert');
    node.setAttribute('data-ero-cart-error', '');
    form.appendChild(node);
    return node;
  }

  function showError(form, message) {
    errorNode(form).textContent = message;
  }

  function clearError(form) {
    var node = form.querySelector('[data-ero-cart-error]');
    if (node) node.textContent = '';
  }

  function setBusy(form, busy) {
    var button = form.querySelector('[data-atc]');
    form.dataset.eroCartBusy = busy ? '1' : '0';
    if (!button) return;
    button.disabled = busy;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function flashAdded(form) {
    var button = form.querySelector('[data-atc]');
    var label = form.querySelector('[data-atc-label]');
    if (!label) return;

    /* The resting label is captured once and stored, so a second press
       inside the 1600ms window cannot bake "ADDED!" in permanently. */
    if (button && !button.dataset.eroCartLabel) {
      button.dataset.eroCartLabel = label.textContent;
    }
    var resting = (button && button.dataset.eroCartLabel) || 'ADD TO BAG';

    label.textContent = 'ADDED!';
    if (form._eroAddedTimer) window.clearTimeout(form._eroAddedTimer);
    form._eroAddedTimer = window.setTimeout(function () {
      label.textContent = resting;
    }, ADDED_MS);
  }

  function submitAdd(form) {
    if (form.dataset.eroCartBusy === '1') return;
    setBusy(form, true);
    clearError(form);

    var body = new FormData(form);
    body.append('sections', SECTIONS);
    body.append('sections_url', window.location.pathname);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok || result.data.status) {
          throw new Error(
            result.data.description || result.data.message || 'Unable to add this item.'
          );
        }
        flashAdded(form);
        /* Still opens compact, with the gift line already in it. */
        return settleGift(result.data.sections).then(function (sections) {
          result.data.sections = sections;
          openWith(result.data, 'compact');
        });
      })
      .catch(function (error) {
        /* Errors surface on the form and the drawer stays shut. */
        showError(form, error.message || 'Unable to add this item. Please try again.');
      })
      .finally(function () {
        setBusy(form, false);
      });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || form.nodeName !== 'FORM') return;
    if (!form.matches(ATC_FORMS)) return;

    event.preventDefault();
    submitAdd(form);
  });

  /* ── Sticky ATC bar bridge ────────────────────────────────────────
     The bar already posts FormData to /cart/add.js, sets its ADDED! glow
     boost and then publishes cartUpdate. Listening for that publish means
     the drawer opens off the bar's existing success path with no edit to
     sections/ero-pdp-sticky-atc.liquid and no second add request.

     Only 'pdp-sticky-atc' opens. 'pdp-cross-sell' refreshes silently, which
     is what keeps the shaker and sample adds from bouncing the drawer. */
  function bindPubSub() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event) return;

      /* Settled first, so the drawer opens with the gift line already in it. */
      if (event.source === 'pdp-sticky-atc') {
        syncGift()
          .then(function () {
            return refreshAndOpen('compact');
          })
          .catch(function (error) {
            console.error(error);
          });
        return;
      }

      /* These sources paint themselves, so they repaint only if the gift moved. */
      if (event.source === 'ero-cart' || event.source === 'cart-items') {
        syncGiftQuiet();
        return;
      }

      syncGift()
        .then(refreshQuiet)
        .catch(function (error) {
          console.error(error);
        });
    });
  }

  /* ── Bag icon ─────────────────────────────────────────────────────
     Dawn's CartDrawer.setHeaderCartIconAccessibility already hijacks
     #cart-icon-bubble and calls open(); this only pins the height.

     Compact, like every other open path. Expanded is now reached ONLY by
     swiping up: no entry point opens the sheet at full height (spec
     section 2). */
  function bindBagIcon() {
    var link = document.getElementById('cart-icon-bubble');
    if (!link) return;

    link.addEventListener('click', function () {
      setSheet('compact');
    });
    link.addEventListener('keydown', function (event) {
      if (event.code && event.code.toUpperCase() === 'SPACE') setSheet('compact');
    });
  }

  /* ══════════════════════════════════════════════════════════════════
     LINE ITEM CONTROLS, NUDGE, ADD-ONS (spec sections 3, 4, 5)

     All delegated from the <cart-drawer> host in the BUBBLE phase. Two
     things fall out of that for free:
       - every control keeps working after a re-render, because the host is
         outside the #CartDrawer subtree that gets replaced;
       - a committed drag can never fire one, because the drag's own
         capture-phase click handler calls stopPropagation on the host and
         the event never reaches the bubble phase. A tap under the 8px slop
         is not suppressed, so it descends and bubbles normally.
     The flavor and frequency dropdowns open and commit on pointerup, not
     click, like the buy box field they mirror. A committed drag holds
     pointer capture on the host, so its pointerup never lands on a field
     or a row, and onDdPointerUp also stands down while a drag is active.
     ══════════════════════════════════════════════════════════════════ */
  var ADDON_ADDED_MS = 1600;

  function lineRow(node) {
    return node ? node.closest('[data-ero-cart-line]') : null;
  }

  function setRowPending(row, pending) {
    if (row) row.classList.toggle('is-pending', !!pending);
  }

  function showLineError(row, message) {
    if (!row) return;
    /* Row-scoped: the error box sits inside its own line row on both
       surfaces, so this never depends on a document-unique id. */
    var text = row.querySelector('.ero-cart__line-error .cart-item__error-text');
    if (text) text.textContent = message;
  }

  /*
     Last plan per variant, for this visit only. Lets Subscribe & Save
     return a line to the plan it was on (e.g. every 8 weeks) after the
     customer tried One-time, instead of the default plan. sessionStorage
     can throw (private mode, blocked storage), so every access is guarded
     and failure just means the default plan is used.
  */
  var LAST_PLAN_KEY = 'ero:cart:last-plan';

  function readLastPlans() {
    try {
      return JSON.parse(window.sessionStorage.getItem(LAST_PLAN_KEY) || '{}') || {};
    } catch (error) {
      return {};
    }
  }

  function rememberPlan(variantId, planId) {
    try {
      var plans = readLastPlans();
      plans[String(variantId)] = String(planId);
      window.sessionStorage.setItem(LAST_PLAN_KEY, JSON.stringify(plans));
    } catch (error) {
      /* storage unavailable: the default plan applies */
    }
  }

  function planOf(row) {
    var plan = row && row.dataset.eroCartPlan;
    return plan ? Number(plan) : null;
  }

  function cartHasVariant(cart, variantId) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i += 1) {
      if (Number(items[i].id) === Number(variantId)) return true;
    }
    return false;
  }

  /*
     Swap a line's variant.

     change.js cannot actually re-point a line at a different variant — its
     `id` identifies WHICH line to change, it does not set one. So the call
     is made as specified, and then the returned cart is inspected: if the
     new variant is not in it the swap did not happen, and the documented
     fallback runs (quantity 0, then add.js with the new id and the same
     plan, then a sections fetch). A thrown error takes the same path. Only
     a failure of the FALLBACK surfaces to the row.
  */
  function swapVariant(row, newVariantId) {
    var line = Number(row.dataset.eroCartLine);
    var qty = Number(row.dataset.eroCartQuantity) || 1;
    var plan = planOf(row);
    var originalVariant = Number(row.dataset.eroCartVariant);
    var removed = false;

    function addVariant(id) {
      var item = { id: Number(id), quantity: qty };
      if (plan) item.selling_plan = plan;
      return postCart('/cart/add.js', { items: [item] });
    }

    /*
       Remove-then-add. The add response already carries the rendered
       sections (postCart puts `sections` and `sections_url` on every call),
       so the chain ends on the LAST mutating response — no trailing GET.

       If the removal succeeded and the add failed, the line has been taken
       out of a cart the customer can still see. The original variant is
       re-added before the error propagates so nothing is silently lost, and
       the message says so.
    */
    function fallback() {
      return postCart('/cart/change.js', { line: line, quantity: 0 })
        .then(function () {
          removed = true;
          return addVariant(newVariantId);
        })
        .then(function (cart) {
          return cart.sections;
        })
        .catch(function (error) {
          if (!removed) throw error;

          return addVariant(originalVariant).then(
            function () {
              throw new Error('Could not switch flavour. Your original item was kept.');
            },
            function () {
              throw new Error(
                'Could not switch flavour and the original could not be restored. Please reload the page.'
              );
            }
          );
        });
    }

    /*
       TWO-ARGUMENT then, deliberately, NOT .catch(fallback).

       A .catch chained after .then also catches whatever the fulfilment
       handler rejects with — so when the first call SUCCEEDED but the swap
       had not happened, fallback() ran inside the .then, and if it rejected
       the .catch ran fallback() a SECOND time. That fired another
       change.js quantity 0 against a line index that had already shifted,
       removing an unrelated line. A rejection handler passed to then() only
       ever sees the original call.
    */
    return postCart('/cart/change.js', {
      line: line,
      id: Number(newVariantId),
      quantity: qty,
      selling_plan: plan
    }).then(function (cart) {
      if (cartHasVariant(cart, newVariantId)) return cart.sections;
      return fallback();
    }, fallback);
  }

  /* Plan changes are a first-class change.js parameter, so no fallback. */
  function swapPlan(row, planId) {
    return postCart('/cart/change.js', {
      line: Number(row.dataset.eroCartLine),
      quantity: Number(row.dataset.eroCartQuantity) || 1,
      selling_plan: planId ? Number(planId) : null
    }).then(function (cart) {
      return cart.sections;
    });
  }

  /*
     ONE apply, and .is-pending always clears.

     The old version cleared pending only in the error path, so any chain
     that resolved without usable sections left the row at
     pointer-events: none / opacity .55 — the whole drawer read as frozen
     while the SERVER cart was already correct, which is why the change only
     showed up once something else forced a re-render (another Add to Bag
     going through renderContents, a cross-sell cartUpdate hitting
     refreshQuiet, or a page load). Dawn's close()/open() do not re-render on
     their own; they only toggle classes and trap focus.

     A resolved-but-empty sections map is now an explicit failure rather
     than a silent no-op, and finally() unlocks the row either way.

     onFail (optional) runs whenever the update does not land: refused
     because the row is already pending, or failed. The dropdowns pass one
     to put their shown value back, since they show the new choice at once.
     onApplied (optional) runs after a successful re-render; the dropdowns
     use it to put focus back on the field the re-render replaced.
  */
  function runLineUpdate(row, work, onFail, onApplied) {
    if (!row) return;
    if (row.classList.contains('is-pending')) {
      if (onFail) onFail();
      return;
    }
    setRowPending(row, true);
    showLineError(row, '');

    work()
      .then(settleGift)
      .then(function (sections) {
        /* The surface's own section must be in the reply ('cart-drawer' in the drawer). */
        var surface = getSurface();
        if (!sections || !surface || !sections[surface.section]) {
          throw new Error('The cart updated but the drawer could not refresh. Please reload.');
        }
        applySections(sections);
        if (onApplied) onApplied();
      })
      .catch(function (error) {
        if (onFail) onFail();
        showLineError(row, error.message || 'Could not update this item.');
      })
      .finally(function () {
        setRowPending(row, false);
      });
  }

  /*
     MAKE IT A BUNDLE. Drops the single line and adds the bundle variant on
     the same plan. Visibility is decided in Liquid from cart.items on every
     render and is never touched here.
  */
  function runNudge(nudge) {
    if (nudge.classList.contains('is-pending')) return;
    nudge.classList.add('is-pending');

    var line = Number(nudge.dataset.eroCartNudgeLine);
    var variant = Number(nudge.dataset.eroCartNudgeVariant);
    var plan = nudge.dataset.eroCartNudgePlan ? Number(nudge.dataset.eroCartNudgePlan) : null;

    postCart('/cart/change.js', { line: line, quantity: 0 })
      .then(function () {
        var item = { id: variant, quantity: 1 };
        if (plan) item.selling_plan = plan;
        return postCart('/cart/add.js', { items: [item] });
      })
      .then(function (cart) {
        return settleGift(cart.sections);
      })
      .then(applySections)
      .catch(function () {
        nudge.classList.remove('is-pending');
        /* The remove may have landed before the add failed. */
        syncGiftQuiet();
      });
  }

  /* ── Add-on cards ─────────────────────────────────────────────────
     The swatch rewrites the card in place — no request — so the CTA always
     posts whatever is currently selected. */
  function selectAddonSwatch(swatch) {
    var card = swatch.closest('[data-ero-cart-addon]');
    if (!card || swatch.disabled) return;

    card.dataset.selectedVariant = swatch.dataset.variantId || '';

    card.querySelectorAll('[data-ero-cart-addon-swatch]').forEach(function (node) {
      var on = node === swatch;
      node.classList.toggle('is-selected', on);
      node.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    var image = card.querySelector('[data-ero-cart-addon-image]');
    if (image && swatch.dataset.variantImage) {
      image.src = swatch.dataset.variantImage;
      image.removeAttribute('srcset');
    }

    var colour = card.querySelector('[data-ero-cart-addon-color]');
    if (colour && swatch.dataset.variantTitle) colour.textContent = swatch.dataset.variantTitle;

    var price = card.querySelector('[data-ero-cart-addon-price]');
    if (price && swatch.dataset.variantPrice) price.textContent = swatch.dataset.variantPrice;
  }

  function addAddon(card) {
    if (card.classList.contains('is-pending')) return;

    var variant = Number(card.dataset.selectedVariant);
    if (!variant) return;

    var key = card.dataset.eroCartAddon;
    var label = card.querySelector('[data-ero-cart-addon-cta-label]');
    var errorNode = card.querySelector('[data-ero-cart-addon-error]');
    var resting = label ? label.textContent : '';

    card.classList.add('is-pending');
    if (errorNode) errorNode.textContent = '';

    postCart('/cart/add.js', {
      items: [
        {
          id: variant,
          quantity: 1,
          properties: { _source: 'cart_drawer_addon' }
        }
      ]
    })
      .then(function (cart) {
        /*
           Render FIRST, then re-mark. The old order deferred applySections
           by the full 1600ms so the label would survive, which meant the
           subtotal, the count and the line list all sat stale for a second
           and a half after the request had already come back — the whole of
           the perceived delay on this path.

           applySections replaces this card, so the ADDED state is re-applied
           to the FRESH node as a data flag and CSS paints it. The flag is
           cleared on a timer, but nothing is waiting on that timer.
        */
        applySections(cart.sections);
        syncGiftQuiet();

        /* Looked up inside the surface that was just re-rendered. */
        var surface = getSurface();
        var fresh = surface && surface.root.querySelector('[data-ero-cart-addon="' + key + '"]');
        if (!fresh) return;

        fresh.setAttribute('data-ero-cart-added', '');
        window.setTimeout(function () {
          fresh.removeAttribute('data-ero-cart-added');
        }, ADDON_ADDED_MS);
      })
      .catch(function (error) {
        if (label) label.textContent = resting;
        if (errorNode) errorNode.textContent = error.message || 'Could not add this.';
      })
      .finally(function () {
        card.classList.remove('is-pending');
      });
  }

  /* ── ERO dropdown: flavor and frequency (6.16 v2 fix C) ───────────
     The cart line's own flavor and frequency dropdowns. The behaviour was
     first mirrored from the buy box's frequency dropdown, which 461c932
     replaced with buttons; this copy is the only dropdown left and is not
     shared with the buy box. Works for any number of lines,
     delegated from the surface root so it survives every re-render, with
     one panel open at a time across all of them.

     Commits run the same paths the selects ran: flavor is swapVariant
     (change.js, then the remove-and-add fallback), frequency is swapPlan
     (change.js with the new selling_plan), both through runLineUpdate. */
  var openDd = null;
  var swallowEscapeUp = false;

  function ddToggle(dd) {
    return dd.querySelector('[data-ero-cart-dd-toggle]');
  }

  function ddList(dd) {
    return dd.querySelector('[data-ero-cart-dd-list]');
  }

  function ddOptions(dd) {
    return Array.prototype.slice.call(dd.querySelectorAll('[role="option"]'));
  }

  /*
     The room the panel may use: the viewport, cut down by every ancestor
     whose overflow is not visible. An ancestor counts to its CONTENT box,
     not its padding box: the drawer's scroller (.ero-cart__items) runs its
     16 bottom padding under the footer, which paints over that strip. On
     /cart no ancestor clips, so only the viewport counts.
  */
  function ddBounds(dd) {
    var top = 0;
    var bottom = window.innerHeight || document.documentElement.clientHeight;
    for (var el = dd.parentElement; el && el !== document.body; el = el.parentElement) {
      var style = window.getComputedStyle(el);
      if (style.overflowY === 'visible') continue;
      var rect = el.getBoundingClientRect();
      top = Math.max(top, rect.top + (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.paddingTop) || 0));
      bottom = Math.min(
        bottom,
        rect.bottom - (parseFloat(style.borderBottomWidth) || 0) - (parseFloat(style.paddingBottom) || 0)
      );
    }
    return { top: top, bottom: bottom };
  }

  /*
     Down by default, as in the buy box. Up when the panel and its 6 offset
     do not fit below the field inside those bounds and there is more room
     above (in the compact sheet the first line's fields sit at the bottom
     of the visible body, so they open up). Measured with the panel already
     showing in the down position, so the offset is read, not restated.
  */
  function placeDd(dd, list) {
    dd.classList.remove('is-up');
    var field = dd.getBoundingClientRect();
    var panel = list.getBoundingClientRect();
    var need = panel.bottom - field.bottom;
    var bounds = ddBounds(dd);
    var below = bounds.bottom - field.bottom;
    var above = field.top - bounds.top;
    if (below < need && above > below) dd.classList.add('is-up');
  }

  function closeDd(dd, returnFocus) {
    if (!dd) return;
    if (openDd === dd) openDd = null;
    dd.classList.remove('is-open', 'is-up');
    var toggle = ddToggle(dd);
    if (!toggle) return;
    toggle.setAttribute('aria-expanded', 'false');
    if (returnFocus && dd.isConnected) toggle.focus({ preventScroll: true });
  }

  function openDdPanel(dd) {
    var toggle = ddToggle(dd);
    var list = ddList(dd);
    if (!toggle || !list || !ddOptions(dd).length) return;
    if (openDd && openDd !== dd) closeDd(openDd, false);

    openDd = dd;
    dd.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    placeDd(dd, list);

    var target = list.querySelector('[aria-selected="true"]') || ddOptions(dd)[0];
    if (target) target.focus({ preventScroll: true });
  }

  /* The shown value and the selected row, set to one option's value. */
  function markDd(dd, value) {
    var label = null;
    ddOptions(dd).forEach(function (option) {
      var on = option.dataset.eroCartDdValue === value;
      option.classList.toggle('is-selected', on);
      option.setAttribute('aria-selected', on ? 'true' : 'false');
      if (on) label = option.textContent.trim();
    });
    var shown = dd.querySelector('[data-ero-cart-select-value]');
    if (shown && label !== null) shown.textContent = label;
  }

  /*
     After the re-render, focus goes back to the same field on the line
     that now carries the result (a flavor swap re-adds the line, so it is
     found by variant and plan, not by index). Only when the re-render is
     what took focus away: if focus moved somewhere real meanwhile, it
     stays there.
  */
  function refocusDd(kind, variant, plan) {
    var active = document.activeElement;
    if (active && active !== document.body) return;
    var surface = getSurface();
    if (!surface) return;
    var rows = surface.root.querySelectorAll('[data-ero-cart-line][data-ero-cart-variant="' + variant + '"]');
    for (var i = rows.length - 1; i >= 0; i -= 1) {
      if ((rows[i].dataset.eroCartPlan || '') !== String(plan || '')) continue;
      var toggle = rows[i].querySelector('[data-ero-cart-dd="' + kind + '"] [data-ero-cart-dd-toggle]');
      if (toggle) toggle.focus({ preventScroll: true });
      return;
    }
  }

  function chooseDd(dd, option) {
    if (!dd || !option || !option.dataset.eroCartDdValue) return;
    var value = option.dataset.eroCartDdValue;
    var current = dd.dataset.eroCartDdCurrent || '';
    var row = lineRow(dd);

    closeDd(dd, true);
    if (!row || value === current) return;

    markDd(dd, value);
    var undo = function () {
      markDd(dd, current);
    };

    if (dd.dataset.eroCartDd === 'flavor') {
      var plan = row.dataset.eroCartPlan || '';
      runLineUpdate(
        row,
        function () {
          return swapVariant(row, value);
        },
        undo,
        function () {
          refocusDd('flavor', value, plan);
        }
      );
      return;
    }

    if (dd.dataset.eroCartDd === 'frequency') {
      var variant = row.dataset.eroCartVariant;
      runLineUpdate(
        row,
        function () {
          return swapPlan(row, value);
        },
        undo,
        function () {
          refocusDd('frequency', variant, value);
        }
      );
    }
  }

  /* Pointer Events only: a tap on the field toggles, a tap on a row
     commits. Stands down while a sheet drag is live. */
  function onDdPointerUp(event) {
    if (drag && drag.active) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    var target = event.target;
    if (!target || !target.closest) return;

    var option = target.closest('[data-ero-cart-dd] [role="option"]');
    if (option) {
      chooseDd(option.closest('[data-ero-cart-dd]'), option);
      return;
    }

    var toggle = target.closest('[data-ero-cart-dd-toggle]');
    if (!toggle) return;
    var dd = toggle.closest('[data-ero-cart-dd]');
    if (!dd) return;
    if (dd.classList.contains('is-open')) closeDd(dd, false);
    else openDdPanel(dd);
  }

  /* The buy box key map. Escape also arms swallowEscapeUp, so the keyup
     that follows cannot reach Dawn's drawer-wide Escape (cart-drawer.js
     closes the whole drawer on keyup). */
  function onDdKeyDown(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var dd = target.closest('[data-ero-cart-dd]');
    if (!dd) return;
    var key = event.key;

    if (target.hasAttribute('data-ero-cart-dd-toggle')) {
      if (key === 'Enter' || key === ' ' || key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault();
        openDdPanel(dd);
      } else if (key === 'Escape' && dd.classList.contains('is-open')) {
        event.preventDefault();
        swallowEscapeUp = true;
        closeDd(dd, true);
      }
      return;
    }

    var options = ddOptions(dd);
    if (!options.length) return;
    var index = options.indexOf(document.activeElement);

    if (key === 'ArrowDown') {
      event.preventDefault();
      options[Math.min(index + 1, options.length - 1)].focus({ preventScroll: true });
    } else if (key === 'ArrowUp') {
      event.preventDefault();
      options[Math.max(index - 1, 0)].focus({ preventScroll: true });
    } else if (key === 'Home') {
      event.preventDefault();
      options[0].focus({ preventScroll: true });
    } else if (key === 'End') {
      event.preventDefault();
      options[options.length - 1].focus({ preventScroll: true });
    } else if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      chooseDd(dd, document.activeElement && document.activeElement.closest('[role="option"]'));
    } else if (key === 'Escape') {
      event.preventDefault();
      swallowEscapeUp = true;
      closeDd(dd, true);
    } else if (key === 'Tab') {
      closeDd(dd, false);
    }
  }

  /* Capture phase on the root, so it runs before Dawn's keyup listener on
     <cart-drawer> and stops the event there. */
  function onDdKeyUp(event) {
    if (!swallowEscapeUp) return;
    if (event.key !== 'Escape' && event.code !== 'Escape') return;
    swallowEscapeUp = false;
    event.stopPropagation();
  }

  /* A press anywhere outside the open dropdown closes it. Document-wide,
     bound once; a panel lost to a re-render is simply forgotten. */
  var ddOutsideBound = false;

  function bindDdOutside() {
    if (ddOutsideBound) return;
    ddOutsideBound = true;
    document.addEventListener('pointerdown', function (event) {
      if (!openDd) return;
      if (!openDd.isConnected) {
        openDd = null;
        return;
      }
      if (!openDd.contains(event.target)) closeDd(openDd, false);
    });
  }

  function onDrawerClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    /* Dropdowns: a click with detail 0 is keyboard or assistive-tech
       activation (a screen reader in browse mode sends a click and no
       keydown). It opens a closed field or commits a row. Pointer taps
       (detail 1 and up) were already handled on pointerup. */
    if (target.closest('[data-ero-cart-dd]')) {
      if (event.detail !== 0) return;
      var ddRow = target.closest('[role="option"]');
      var ddHost = target.closest('[data-ero-cart-dd]');
      if (ddRow) chooseDd(ddHost, ddRow);
      else if (target.closest('[data-ero-cart-dd-toggle]') && !ddHost.classList.contains('is-open')) openDdPanel(ddHost);
      return;
    }

    var planPill = target.closest('[data-ero-cart-plan-option]');
    if (planPill) {
      if (planPill.classList.contains('is-selected')) return;
      var prow = lineRow(planPill);
      var pid = planPill.dataset.eroCartPlanOption;
      if (prow) {
        var variantKey = prow.dataset.eroCartVariant;
        if (!pid && prow.dataset.eroCartPlan) {
          rememberPlan(variantKey, prow.dataset.eroCartPlan);
        } else if (pid) {
          var remembered = readLastPlans()[String(variantKey)];
          var validIds = (planPill.dataset.eroCartPlanIds || '').split(',');
          if (remembered && validIds.indexOf(remembered) !== -1) pid = remembered;
        }
        runLineUpdate(prow, function () {
          return swapPlan(prow, pid);
        });
      }
      return;
    }

    if (target.closest('[data-ero-cart-nudge-add]')) {
      var nudge = target.closest('[data-ero-cart-nudge]');
      if (nudge) runNudge(nudge);
      return;
    }

    var swatch = target.closest('[data-ero-cart-addon-swatch]');
    if (swatch) {
      selectAddonSwatch(swatch);
      return;
    }

    if (target.closest('[data-ero-cart-addon-add]')) {
      var card = target.closest('[data-ero-cart-addon]');
      if (card) addAddon(card);
    }
  }

  /* ── Sheet drag ───────────────────────────────────────────────────
     Pointer Events only: no mouse/touch pairs anywhere. Listeners are
     delegated from the <cart-drawer> host, which renderContents never
     replaces, so they survive every re-render.

     Header drags always count. Body drags count only at compact, where the
     body is overflow: hidden and has nothing of its own to scroll; once
     expanded the body is a scroller and the gesture belongs to it. */
  var drag = null;

  function onPointerDown(event) {
    if (!isMobile()) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    var drawer = getDrawer();
    if (!drawer || !drawer.classList.contains('active')) return;
    if (!event.target || !event.target.closest) return;

    /* EVERY pointerdown on the sheet records a POTENTIAL drag, whatever it
       lands on. The old interactive-target gate rejected the two biggest
       surfaces in a line item — the flavour/plan pills and the product name
       link — so most of the compact body could not be dragged at all.

       Nothing is committed here. The gesture only becomes a real drag once
       pointermove passes DRAG_SLOP, and until then a tap behaves exactly as
       before: no capture is taken, no click is suppressed. */
    var inner = event.target.closest('.drawer__inner');
    var onHead = event.target.closest('.ero-cart__head');
    var onBody = event.target.closest('.ero-cart__items');
    var expanded = drawer.classList.contains('is-expanded');

    /* At expanded the body is a real scroller and the gesture is ITS gesture,
       with one exception: a downward drag that starts at scrollTop 0 has
       nothing to scroll, so it shrinks the sheet instead. Nothing here ever
       calls preventDefault on pointermove — native scrolling stays intact. */
    var startScrollTop = onBody ? onBody.scrollTop : 0;

    if (!inner) return;
    if (onBody && expanded && startScrollTop > 0) return;

    drag = {
      id: event.pointerId,
      startY: event.clientY,
      lastY: event.clientY,
      lastT: event.timeStamp || 0,
      prevY: event.clientY,
      prevT: event.timeStamp || 0,
      active: false,
      fromBody: !!onBody && !onHead,
      atScrollTop: startScrollTop === 0
    };
  }

  function onPointerMove(event) {
    if (!drag || event.pointerId !== drag.id) return;

    drag.prevY = drag.lastY;
    drag.prevT = drag.lastT;
    drag.lastY = event.clientY;
    drag.lastT = event.timeStamp || drag.lastT;

    var delta = drag.lastY - drag.startY;

    if (!drag.active && Math.abs(delta) > DRAG_SLOP) {
      /* On the scroller at expanded only a DOWNWARD gesture can become a
         drag; upward is the scroller's own and must stay native. */
      var drawer = getDrawer();
      var expanded = !!drawer && drawer.classList.contains('is-expanded');
      if (!(drag.fromBody && expanded && delta <= 0)) {
        drag.active = true;

        /* Capture only once the gesture is committed. Taking it at
           pointerdown would retarget the click to <cart-drawer> and break
           every tap on a pill, the quantity input, Remove and the CTAs. */
        try {
          if (drawer && drawer.setPointerCapture) drawer.setPointerCapture(event.pointerId);
        } catch (error) {
          /* Capture is best-effort; the window listeners still see the drag. */
        }

        var innerStart = drawer && drawer.querySelector('.drawer__inner');
        if (innerStart) beginFollow(drag, drawer, innerStart);
      }
    }

    if (drag.active) {
      var d2 = getDrawer();
      var inner2 = d2 && d2.querySelector('.drawer__inner');
      if (inner2) follow(drag, inner2);
    }
  }

  /*
     pointercancel is treated exactly like pointerup, using the last known
     delta. iOS fires it the moment the compositor decides a touch belongs to
     scrolling, and the old handler threw the drag away at that point, which
     is what made swipe-up-at-compact do nothing at all.
  */
  function finishDrag(event, cancelled) {
    if (!drag || event.pointerId !== drag.id) return;

    var started = drag;
    drag = null;

    var drawer = getDrawer();
    if (!drawer) return;

    try {
      if (drawer.hasPointerCapture && drawer.hasPointerCapture(event.pointerId)) {
        drawer.releasePointerCapture(event.pointerId);
      }
    } catch (error) {
      /* Already released. */
    }

    /* A cancel event's own coordinates can be stale, so the last move wins. */
    var endY = cancelled ? started.lastY : event.clientY;
    if (typeof endY !== 'number') endY = started.lastY;
    if (typeof endY !== 'number') endY = started.startY;

    var delta = endY - started.startY;
    var expanded = drawer.classList.contains('is-expanded');

    /* A gesture that never passed the slop is a tap: leave the sheet alone
       and let the click through untouched. */
    if (!started.active) return;

    endFollow(drawer, drawer.querySelector('.drawer__inner'));

    /* Velocity from the last two samples. Negative is upward. A flick that
       never covered 24px still snaps if it was fast enough. */
    var dt = started.lastT - started.prevT;
    var vy = dt > 0 ? (started.lastY - started.prevY) / dt : 0;

    /* The browser will now fire a click on whatever the finger came down on.
       Swallow exactly one, in the capture phase, so a swipe across a pill or
       the product link cannot also activate it. */
    suppressClickUntil = (event.timeStamp || 0) + CLICK_SUPPRESS_MS;

    /* Distance OR velocity: either alone commits the snap. */
    var goUp = delta <= -DRAG_THRESHOLD || vy <= -VELOCITY_THRESHOLD;
    var goDown = delta >= DRAG_THRESHOLD || vy >= VELOCITY_THRESHOLD;

    if (goUp && !goDown) {
      /* An upward drag inside an expanded body is a scroll, not a grow. */
      if (started.fromBody && expanded) return;
      drawer.classList.add('is-expanded');
      return;
    }

    if (goDown && !goUp) {
      /* From the body at expanded this only fires when the scroller was
         already at the top, which onPointerDown has verified. */
      if (started.fromBody && expanded && !started.atScrollTop) return;

      /* Expanded shrinks to compact; compact closes. */
      if (expanded) drawer.classList.remove('is-expanded');
      else closeDrawer();
    }
  }

  function onPointerUp(event) {
    finishDrag(event, false);
  }

  function onPointerCancel(event) {
    finishDrag(event, true);
  }

  /*
     Click suppression after a drag. Capture phase on the host, so it lands
     before the target's own handler and before the close button's inline
     onclick. Time-boxed rather than a bare boolean: if a gesture ends
     without a click (pointercancel, a drag off the sheet), a latched flag
     would eat the NEXT unrelated tap instead.
  */
  var suppressClickUntil = 0;

  function onCaptureClick(event) {
    if (!suppressClickUntil) return;
    var now = event.timeStamp || 0;
    if (now > suppressClickUntil) {
      suppressClickUntil = 0;
      return;
    }
    suppressClickUntil = 0;
    event.preventDefault();
    event.stopPropagation();
  }

  /* Delegated control clicks on a surface root. The drawer binds through
     bindDrag (after its capture-phase click guard); the /cart page root has
     no drag, so it binds directly. Once per root element. The dropdowns
     bind here too: pointerup and keydown in the bubble phase, the Escape
     keyup guard in the capture phase (see onDdKeyUp). */
  function bindControls(root) {
    if (!root || root._eroCartControlsBound) return;
    root._eroCartControlsBound = true;
    root.addEventListener('click', onDrawerClick);
    root.addEventListener('pointerup', onDdPointerUp);
    root.addEventListener('keydown', onDdKeyDown);
    root.addEventListener('keyup', onDdKeyUp, true);
    bindDdOutside();
  }

  /* The /cart page surface only. On a page with the drawer this is a no-op:
     the drawer's controls are bound by bindDrag. */
  function bindPageControls() {
    var surface = getSurface();
    if (surface && !surface.isDrawer) bindControls(surface.root);
  }

  var windowDragBound = false;

  function bindDrag() {
    var drawer = getDrawer();
    if (!drawer || drawer._eroCartDragBound) return;
    drawer._eroCartDragBound = true;

    drawer.addEventListener('pointerdown', onPointerDown);
    drawer.addEventListener('click', onCaptureClick, true);

    /* Bubble phase, so onCaptureClick's stopPropagation on a committed drag
       keeps the event from ever getting here. Registered after it, exactly
       as before; bindControls only adds the once-per-root flag. */
    bindControls(drawer);

    /* Window listeners are global, so they bind once even when a theme
       editor re-render hands us a new <cart-drawer>. */
    if (windowDragBound) return;
    windowDragBound = true;
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
  }

  /* Crossing the breakpoint with a sheet open would otherwise leave
     is-expanded on the desktop panel, where it means nothing. */
  function bindBreakpoint() {
    var mq = window.matchMedia(MOBILE_QUERY);
    var onChange = function () {
      if (!mq.matches) {
        var drawer = getDrawer();
        if (drawer) drawer.classList.remove('is-expanded');
      }
    };
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
    else if (typeof mq.addListener === 'function') mq.addListener(onChange);
  }

  /* ── /cart page: quantity and Remove (6.16) ───────────────────────
     Dawn's CartItems engine (assets/cart.js) pointed at the page section,
     the way CartDrawerItems (assets/cart-drawer.js) points it at the
     drawer. Quantity changes reach it as the bubbling change event from
     <quantity-input>, exactly as in the drawer.

     Remove needs one extra step. Dawn's <cart-remove-button> looks only for
     a cart-items or cart-drawer-items ancestor, finds neither here and
     would throw. A capture-phase click on this element runs before the
     button's own listener, stops the event there and makes the same
     updateQuantity(index, 0) call, so cart.js stays untouched.

     A class because CartItems is one. cart.js is deferred ahead of this file
     everywhere this element exists, so CartItems is defined by now. */
  function definePageItems() {
    if (typeof CartItems !== 'function' || !window.customElements) return;
    if (window.customElements.get('ero-cart-page-items')) return;

    window.customElements.define(
      'ero-cart-page-items',
      class EroCartPageItems extends CartItems {
        constructor() {
          super();
          this.addEventListener('click', this.onRemoveClick.bind(this), true);
        }

        onRemoveClick(event) {
          var button = event.target && event.target.closest && event.target.closest('cart-remove-button');
          if (!button || !this.contains(button)) return;
          event.preventDefault();
          event.stopPropagation();
          this.updateQuantity(button.dataset.index, 0, event);
        }

        sectionId() {
          var root = this.closest('[data-ero-cart-root]');
          return root ? root.dataset.eroCartSection : '';
        }

        getSectionsToRender() {
          return [
            { id: 'EroCartPage', section: this.sectionId(), selector: '#EroCartPage' },
            { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
          ];
        }

        /* Re-render the page block when something else changes the cart.
           CartItems already skips its own 'cart-items' publishes. */
        onCartUpdate() {
          var sectionId = this.sectionId();
          if (!sectionId) return Promise.resolve();

          return fetch(window.routes.cart_url + '?section_id=' + encodeURIComponent(sectionId))
            .then(function (response) {
              return response.text();
            })
            .then(function (text) {
              var fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('#EroCartPage');
              var block = document.getElementById('EroCartPage');
              if (fresh && block) block.innerHTML = fresh.innerHTML;
            })
            .catch(function (error) {
              console.error(error);
            });
        }
      }
    );
  }

  /* ── Express checkout re-init (6.16 v2 prompt 3) ───────────────────
     Shopify's accelerated checkout buttons are web components, and the
     Section Rendering API docs have them come back by themselves when a
     re-render re-inserts them. The one thing an innerHTML swap cannot do is
     run a <script>: one arriving that way stays inert. So after any
     re-render of a surface, every script inside .ero-cart__express that has
     not run yet is swapped for a fresh copy, which the browser runs as if
     the page had parsed it. If the wallet output carries no script, this
     does nothing. Scripts in the page at load already ran and are only
     marked.

     A MutationObserver on the surface root rather than a call in each
     render path, because four paths replace the foot: applySections,
     Dawn's renderContents, Dawn's quantity and Remove (cart.js), and the
     /cart page's onCartUpdate. The swap itself mutates the tree, so the
     fresh copy is marked first and the observer's second pass skips it. */
  function activateExpress(root) {
    var scripts = root.querySelectorAll('.ero-cart__express script');
    for (var i = 0; i < scripts.length; i += 1) {
      var old = scripts[i];
      if (old._eroExpressRan) continue;
      var fresh = document.createElement('script');
      for (var a = 0; a < old.attributes.length; a += 1) {
        fresh.setAttribute(old.attributes[a].name, old.attributes[a].value);
      }
      fresh.text = old.textContent;
      fresh._eroExpressRan = true;
      old.parentNode.replaceChild(fresh, old);
    }
  }

  /* ── Free shipping bar (6.16 v2 prompt 6) ─────────────────────────
     Every re-render inserts a fresh bar already at its final width, which
     would jump. So each new rail ([data-ero-cart-ship-fill], which carries
     --ero-ship-fill for both the fill's width and the knob's position)
     starts at the value the previous bar showed in the same placement
     ('stack' or 'card') and moves to its own on the next frame; the 400ms
     width and left transitions in the CSS animate fill and knob in
     lockstep (both off under reduced motion). A bar that appears where
     there was none grows from 0. Bars present at page load are only
     recorded.

     The text is an aria-live polite region, but a region that arrives
     already filled is not announced, and each re-render inserts a new one.
     So when the message differs from the last one, the same text is
     written into the region again shortly after insertion: a fresh text
     node is an addition to an existing live region, which screen readers
     announce, and nothing changes on screen. */
  var shipLast = {};
  var SHIP_ANNOUNCE_MS = 150;

  function syncShip(root, initial) {
    var bars = root.querySelectorAll('[data-ero-cart-ship]');
    var present = {};

    for (var i = 0; i < bars.length; i += 1) {
      var bar = bars[i];
      var key = bar.getAttribute('data-ero-cart-ship');
      present[key] = true;
      if (bar._eroShipSeen) continue;
      bar._eroShipSeen = true;

      var fill = bar.querySelector('[data-ero-cart-ship-fill]');
      var text = bar.querySelector('[data-ero-cart-ship-text]');
      var to = fill ? fill.getAttribute('data-ero-cart-ship-fill') + '%' : null;
      var msg = text ? text.textContent : '';
      var last = shipLast[key];
      shipLast[key] = { fill: to, msg: msg };
      if (initial) continue;

      var from = last ? last.fill : '0%';
      if (fill && to && from !== to) {
        fill.style.setProperty('--ero-ship-fill', from);
        void fill.offsetWidth;
        animateShipFill(fill, to);
      }

      if (text && (!last || last.msg !== msg)) announceShip(text, msg);
    }

    /* A placement whose bar is gone (cart emptied, sample-only) is
       forgotten, so the next bar there grows from 0. */
    Object.keys(shipLast).forEach(function (key) {
      if (!present[key]) delete shipLast[key];
    });
  }

  function animateShipFill(fill, to) {
    window.requestAnimationFrame(function () {
      fill.style.setProperty('--ero-ship-fill', to);
    });
  }

  function announceShip(text, msg) {
    window.setTimeout(function () {
      if (text.isConnected) text.textContent = msg;
    }, SHIP_ANNOUNCE_MS);
  }

  /* One observer per surface root, for the two things a re-render brings
     that the markup alone cannot finish: inert express-checkout scripts
     and the shipping bar's animation and announcement. */
  function watchSurface(root) {
    if (!root || root._eroSurfaceWatched || typeof window.MutationObserver !== 'function') return;
    root._eroSurfaceWatched = true;
    var initial = root.querySelectorAll('.ero-cart__express script');
    for (var i = 0; i < initial.length; i += 1) initial[i]._eroExpressRan = true;
    syncShip(root, true);
    new MutationObserver(function () {
      activateExpress(root);
      syncShip(root, false);
    }).observe(root, { childList: true, subtree: true });
  }

  function init() {
    /* First, and before Dawn upgrades the element. */
    portalToBody();
    bindPubSub();
    bindBagIcon();
    bindDrag();
    bindPageControls();
    definePageItems();
    watchSurface(getDrawer());
    watchSurface(document.querySelector('[data-ero-cart-root]'));
    /* Drawer-only from here. Each is a no-op when getDrawer() is null, which
       is exactly the !isDrawer case (bindBreakpoint still adds its listener,
       whose body does nothing without a drawer). The sheet outline is pure
       CSS (6.16 v2), so nothing here paints an edge any more. */
    bindScrollLock();
    bindBreakpoint();
    syncEmptyState();
    /* Both surfaces. The sync also covers a cart restored from an earlier visit. */
    document.addEventListener('click', onGiftCheckoutClick);
    syncGiftQuiet();
  }

  /* The theme editor re-renders the section wrapper, which puts a second
     <cart-drawer> back inside it. Re-portal and re-bind against the survivor. */
  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target || !event.target.querySelector) return;
    if (!event.target.querySelector('cart-drawer')) {
      /* A theme editor re-render of the /cart page section brings a fresh,
         unbound root. Any other section finds none and returns as before. */
      var pageRoot = event.target.querySelector('[data-ero-cart-root]');
      if (pageRoot) {
        bindControls(pageRoot);
        watchSurface(pageRoot);
      }
      return;
    }
    portalToBody();
    bindDrag();
    watchSurface(getDrawer());
    bindScrollLock();
    syncEmptyState();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
